/**
 * Dev-only FPS / zoom stats panel shown above the FPS pill in the debug stack.
 */

import React, { useMemo } from "react";
import { getUIColorPalette } from "../../theme/ColorPalette";

export interface FpsDebugOverlayStats {

    /** Current map render FPS. */
    "renderFps": number;

    /** Configured max render FPS cap. */
    "maxRenderFps": number;

    /** Current bus layer FPS. */
    "busFps": number;

    /** Configured max bus FPS cap. */
    "maxBusFps": number;

    /** Current map zoom level. */
    "zoomLevel": number;

    /** Current map pitch in degrees. */
    "pitchLevel": number;
}

export interface FpsDebugOverlayProps {

    /** Live map and bus FPS metrics. */
    "stats": FpsDebugOverlayStats;

    /** Active navigation render FPS. */
    "navigationFps": number;

    /** Max navigation FPS cap. */
    "maxNavigationFps": number;

    /** Enables dark palette variants when true. */
    "isDarkUi": boolean;
}

/**
 * Renders the monospace FPS debug panel (non-interactive).
 *
 * @param props Metrics and theme flag.
 * @returns FPS stats panel for the dev stack.
 */
const FpsDebugOverlay: React.FC<FpsDebugOverlayProps> = ( {
    stats,
    navigationFps,
    maxNavigationFps,
    isDarkUi,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );

    return (
        <div
            className="rounded-lg border px-3 py-2 font-mono text-xs backdrop-blur-sm"
            style={{
                "pointerEvents":   "none",
                "backgroundColor": palette.overlayCardBackgroundStrong,
                "color":           palette.overlayTextPrimary,
                "borderColor":     palette.overlayBorder,
            }}
        >
            <div className="flex flex-col gap-1">
                <div className="flex justify-between gap-4">
                    <span style={{ "color": palette.overlayTextSecondary }}>
                        Render FPS:
                    </span>
                    <span style={{ "color": isDarkUi ? "#4ade80" : "#166534" }}>
                        {stats.renderFps}
                        {" / "}
                        {stats.maxRenderFps}
                    </span>
                </div>
                <div className="flex justify-between gap-4">
                    <span style={{ "color": palette.overlayTextSecondary }}>
                        Bus FPS:
                    </span>
                    <span style={{ "color": isDarkUi ? "#facc15" : "#854d0e" }}>
                        {stats.busFps}
                        {" / "}
                        {stats.maxBusFps}
                    </span>
                </div>
                <div className="flex justify-between gap-4">
                    <span style={{ "color": palette.overlayTextSecondary }}>
                        Nav FPS:
                    </span>
                    <span style={{ "color": isDarkUi ? "#c084fc" : "#6b21a8" }}>
                        {navigationFps}
                        {" / "}
                        {maxNavigationFps}
                    </span>
                </div>
                <div className="flex justify-between gap-4">
                    <span style={{ "color": palette.overlayTextSecondary }}>
                        Zoom:
                    </span>
                    <span style={{ "color": isDarkUi ? "#60a5fa" : "#1d4ed8" }}>
                        {stats.zoomLevel.toFixed( 2 )}
                    </span>
                </div>
                <div className="flex justify-between gap-4">
                    <span style={{ "color": palette.overlayTextSecondary }}>
                        Pitch:
                    </span>
                    <span style={{ "color": isDarkUi ? "#22d3ee" : "#0e7490" }}>
                        {stats.pitchLevel.toFixed( 2 )}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default FpsDebugOverlay;
